// Registers the service worker that makes FamConnect26 installable and lets it
// open offline. Registration is production-only so the dev server keeps serving
// fresh files on every reload.

import { isStandalone } from './Utils/pwaInstall';

const BUILD_RELOAD_KEY = 'famconnect:reloaded-for-build';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname)
);

// The running page's build against the one the server names in index.html.
async function newerBuild() {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/index.html`, { cache: 'no-store' });
  if (!response.ok) return null;
  const [bundle] = (await response.text()).match(/static\/js\/main\.\w+\.js/) || [];
  return bundle && !document.querySelector(`script[src*="${bundle}"]`) ? bundle : null;
}

function reloadedFor(bundle) {
  try {
    return window.sessionStorage.getItem(BUILD_RELOAD_KEY) === bundle;
  } catch (error) {
    return false;
  }
}

function rememberReload(bundle) {
  try {
    window.sessionStorage.setItem(BUILD_RELOAD_KEY, bundle);
  } catch (error) {
    // Private mode can refuse storage; the reload still happens
  }
}

// iOS resumes an installed app where it left off instead of opening it again, so
// it can run an old build for days after a deploy. service-worker.js does not
// change from build to build, so the worker never reports one either; the check
// has to read index.html. Reloads once per build in case the page comes back old.
function watchForNewBuild() {
  const check = () => {
    if (document.visibilityState !== 'visible') return;
    // A half-typed form is worth more than the update; the next resume gets it.
    if (document.activeElement?.matches('input, textarea, [contenteditable]')) return;
    newerBuild()
      .then((bundle) => {
        if (!bundle || reloadedFor(bundle)) return;
        rememberReload(bundle);
        window.location.reload();
      })
      .catch(() => undefined); // offline: stay on what is running
  };
  check();
  document.addEventListener('visibilitychange', check);
}

export function register() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  // A service worker under a different origin than the page can never control it.
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener('load', () => {
    if (isStandalone()) watchForNewBuild();

    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.onstatechange = () => {
            if (installing.state !== 'installed') return;
            if (navigator.serviceWorker.controller) {
              // A newer build is ready; it takes over on the next full reload.
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          };
        };
      })
      .catch((error) => {
        console.error('Service worker registration failed:', error);
      });
  });
}

export function unregister() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch(() => undefined);
}

export { isLocalhost };
